import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { InputLabelOutlined } from 'common/components/fields/fields';
import { cpfMask, phoneMask } from 'helpers/masks';
import { FORM_RULES } from 'helpers/validations';
import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, reduxForm, reset } from 'redux-form';
import {
  clearInviteError,
  createGuest,
  fetchEligibleDependents,
} from '../guestsActions';

const FORM_NAME = 'inviteGuestForm';
const PRIMARY_COLOR = '#1976d2';
const PRIMARY_COLOR_HOVER_BG = 'rgba(25, 118, 210, 0.04)';
const SUBMIT_COLOR = '#5759FB';
const SUBMIT_COLOR_HOVER = '#4547dc';
const SUBMIT_COLOR_DISABLED = '#c0c0d8';
const FIELD_FOCUS_COLOR = '#2D95EF';

const useStyles = makeStyles(() => ({
  formScope: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: `${FIELD_FOCUS_COLOR} !important`,
    },
    '& label.Mui-focused': {
      color: `${FIELD_FOCUS_COLOR} !important`,
    },
  },
  title: {
    fontSize: 22,
    fontWeight: 600,
    textAlign: 'center',
    color: '#1f1f1f',
  },
  cancelButton: {
    color: `${PRIMARY_COLOR} !important`,
    borderColor: `${PRIMARY_COLOR} !important`,
    borderRadius: '4px !important',
    padding: '8px 24px !important',
    textTransform: 'none !important',
    backgroundColor: '#ffffff !important',
    '&:hover': {
      backgroundColor: `${PRIMARY_COLOR_HOVER_BG} !important`,
      borderColor: `${PRIMARY_COLOR} !important`,
    },
    '&:focus': { outline: 'none !important' },
    '&::-moz-focus-inner': { border: 0 },
  },
  submitButton: {
    backgroundColor: `${SUBMIT_COLOR} !important`,
    color: '#ffffff !important',
    borderRadius: '4px !important',
    padding: '8px 32px !important',
    textTransform: 'none !important',
    boxShadow: 'none !important',
    '&:hover': { backgroundColor: `${SUBMIT_COLOR_HOVER} !important` },
    '&:focus': { outline: 'none !important' },
    '&::-moz-focus-inner': { border: 0 },
    '&.Mui-disabled': {
      backgroundColor: `${SUBMIT_COLOR_DISABLED} !important`,
      color: '#ffffff !important',
    },
  },
}));

const ERROR_TITLE = {
  cpf_already_registered: 'CPF já cadastrado',
  friend_limit_reached: 'Limite de amigos atingido',
  invalid_cpf: 'CPF inválido',
  guest_feature_disabled: 'Funcionalidade indisponível',
  inviter_inactive: 'Sua conta não está ativa',
  invalid_dependent_id: 'Selecione um dependente',
  dependent_not_eligible: 'Dependente não elegível',
  dependent_already_invited: 'Dependente já convidado',
  dependent_provider_unavailable: 'Serviço de dependentes indisponível',
};

const SelectField = ({ input, meta, label, children, disabled }) => (
  <FormControl
    variant="outlined"
    fullWidth
    error={meta && meta.touched && !!meta.error}
    disabled={disabled}
  >
    <InputLabel>{label}</InputLabel>
    <Select {...input} label={label}>
      {children}
    </Select>
  </FormControl>
);

function InviteGuestModal({
  open,
  onClose,
  initialType,
  guests,
  createGuest,
  clearInviteError,
  fetchEligibleDependents,
  formValues,
  handleSubmit,
  resetForm,
}) {
  const classes = useStyles();
  const type = initialType || 'friend';
  const isDependent = type === 'dependent';

  useEffect(() => {
    if (!open) {
      clearInviteError();
    }
  }, [open, clearInviteError]);

  useEffect(() => {
    if (open && isDependent) {
      fetchEligibleDependents();
    }
  }, [open, isDependent, fetchEligibleDependents]);

  const dependents = guests.eligibleDependents.items || [];
  const dependentsLoading = guests.eligibleDependents.loading;
  const dependentsError = guests.eligibleDependents.error;

  const selectedDependent = useMemo(
    () =>
      dependents.find(
        (d) => String(d.id) === String(formValues?.external_dependent_id)
      ) || null,
    [dependents, formValues?.external_dependent_id]
  );

  const onSubmit = (values) => {
    const payload = isDependent
      ? {
          type: 'dependent',
          external_dependent_id: values.external_dependent_id,
          name: selectedDependent ? selectedDependent.name : '',
          cpf: values.cpf,
          email: values.email,
          phone: values.phone,
        }
      : {
          type: 'friend',
          name: values.name,
          cpf: values.cpf,
          email: values.email,
          phone: values.phone,
        };
    createGuest(payload, {
      onSuccess: () => {
        resetForm();
        onClose();
      },
    });
  };

  const handleClose = () => {
    if (guests.inviteLoading) return;
    resetForm();
    onClose();
  };

  const err = guests.inviteError;
  const disableSubmit =
    guests.inviteLoading ||
    (isDependent && dependents.length === 0 && !dependentsLoading);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { borderRadius: 8 } }}
    >
      <Box style={{ background: '#ffffff', padding: 24 }}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className={classes.formScope}
        >
          <Box mb={2}>
            <Typography className={classes.title}>
              {isDependent ? 'Convidar dependente' : 'Convidar amigo'}
            </Typography>
          </Box>

          {err && (
            <Box
              my={2}
              p={2}
              style={{
                backgroundColor: '#fdecea',
                border: '1px solid #f5c2c0',
                borderRadius: 4,
              }}
            >
              <Typography variant="subtitle2" style={{ color: '#a4231a' }}>
                {ERROR_TITLE[err.code] || 'Não foi possível enviar o convite'}
              </Typography>
              <Typography variant="body2" style={{ color: '#a4231a' }}>
                {err.error}
              </Typography>
            </Box>
          )}

          {isDependent ? (
            <Box mb={2}>
              {dependentsLoading ? (
                <Box display="flex" alignItems="center" gridGap={8}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">
                    Carregando dependentes...
                  </Typography>
                </Box>
              ) : dependentsError ? (
                <Typography variant="body2" color="error">
                  {dependentsError.error ||
                    'Não foi possível carregar a lista de dependentes.'}
                </Typography>
              ) : dependents.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  Nenhum dependente disponível para convite.
                </Typography>
              ) : (
                <Field
                  component={SelectField}
                  name="external_dependent_id"
                  label="Dependente"
                  validate={[FORM_RULES.required]}
                >
                  {dependents.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name} {d.parentesco ? `(${d.parentesco})` : ''}
                    </MenuItem>
                  ))}
                </Field>
              )}
            </Box>
          ) : (
            <Box mb={2}>
              <Field
                component={InputLabelOutlined}
                name="name"
                label="Nome"
                validate={[FORM_RULES.required]}
                autoComplete="off"
              />
            </Box>
          )}

          <Box mb={2}>
            <Field
              component={InputLabelOutlined}
              name="cpf"
              label="CPF"
              type="tel"
              validate={[FORM_RULES.required]}
              autoComplete="off"
              {...cpfMask}
            />
          </Box>

          <Box mb={2}>
            <Field
              component={InputLabelOutlined}
              name="email"
              label="E-mail"
              type="email"
              validate={[FORM_RULES.required, FORM_RULES.email]}
              autoComplete="off"
            />
          </Box>

          <Box mb={3}>
            <Field
              component={InputLabelOutlined}
              name="phone"
              label="Telefone"
              type="tel"
              validate={[FORM_RULES.required]}
              autoComplete="off"
              {...phoneMask}
            />
          </Box>

          <Box display="flex" justifyContent="flex-end" gridGap={12}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={guests.inviteLoading}
              className={classes.cancelButton}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={disableSubmit}
              className={classes.submitButton}
              startIcon={
                guests.inviteLoading ? (
                  <CircularProgress size={16} style={{ color: '#fff' }} />
                ) : null
              }
            >
              {guests.inviteLoading ? 'Enviando...' : 'Enviar'}
            </Button>
          </Box>
        </form>
      </Box>
    </Dialog>
  );
}

const mapStateToProps = (state) => ({
  guests: state.guests,
  formValues: state.form?.[FORM_NAME]?.values || {},
});
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      createGuest,
      clearInviteError,
      fetchEligibleDependents,
      resetForm: () => reset(FORM_NAME),
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(reduxForm({ form: FORM_NAME, enableReinitialize: true })(InviteGuestModal));
